import type React from "react";

import type { TicketAssigneeFilterModeDto } from "@/api/types";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { Select } from "@/design-system/Select";
import { SplitField } from "@/product-patterns/tickets/SplitField";
import { useTranslation } from "@/translations";

export type TicketAssigneeFilterMode = TicketAssigneeFilterModeDto;
export const TICKET_ASSIGNEE_UNASSIGNED_ID = "unassigned";

export function mapTicketAssigneeIdsToApi(ids?: string[]): string[] | undefined {
  return ids?.map((id) => (id === TICKET_ASSIGNEE_UNASSIGNED_ID ? "" : id));
}

export type TicketAssigneeFilterOption = {
  id: string;
  fullName: string;
  email?: string | null;
};

type Props = {
  items: readonly TicketAssigneeFilterOption[];
  selectedIds?: string[];
  mode?: TicketAssigneeFilterMode;
  onChange: (ids: string[]) => void;
  onModeChange: (mode: TicketAssigneeFilterMode) => void;
  onSearchChange?: (search: string) => void;
  id?: string;
};

const modes = ["include", "exclude"] as const;

/**
 * Selects assignees to include in or exclude from ticket results. The unassigned option uses a route-safe sentinel
 * that is mapped to the API's empty assignee ID when a ticket query is created.
 */
export function TicketAssigneeFilter({
  items,
  selectedIds,
  mode = "include",
  onChange,
  onModeChange,
  onSearchChange,
  id,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const unassigned: TicketAssigneeFilterOption = {
    id: TICKET_ASSIGNEE_UNASSIGNED_ID,
    fullName: t("page.tickets.header.filter.assignee.unassigned"),
  };
  const options = [unassigned, ...items];

  return (
    <SplitField
      leading={
        <Select
          id={id}
          items={modes}
          selected={mode}
          onChange={(value) => onModeChange(value ?? "include")}
          renderOption={(value) =>
            t(
              value === "exclude"
                ? "page.tickets.filter-drawer.assignee.operator.exclude"
                : "page.tickets.filter-drawer.assignee.operator.include",
            )
          }
          keySelector={(value) => value}
        />
      }
    >
      <CheckboxMultiSelect
        renderMoreSelected={(count) => t("common.label.x-more", { count })}
        id={id ? `${id}-assignees` : undefined}
        aria-label={t("page.tickets.filter-drawer.assignee.label")}
        items={options}
        selected={options.filter((option) => selectedIds?.includes(option.id))}
        onChange={(selected) => onChange(selected.map(({ id }) => id))}
        placeholder={t("page.tickets.filter-drawer.assignee.placeholder")}
        searchPlaceholder={t("page.tickets.filter-drawer.assignee.search-placeholder")}
        searchableFieldSelector={(option) => [option.fullName, option.email ?? ""]}
        onSearchChange={onSearchChange}
        renderOption={(option) => option.fullName}
        keySelector={(option) => option.id}
      />
    </SplitField>
  );
}
